import {
  TableContainer,
  TableHead,
  TableCell,
  Table,
  TableRow,
  TableBody,
  CircularProgress,
  Alert,
  Typography,
  Box,
} from '@mui/material';
import { useApi, LoadingState } from '../../hooks/useApi';
import { formatDateTime } from '../../utils/formatDateTimes';
import { AuditEventType } from '../../../../shared/types';
import { useState } from 'react';
import AutomaticRunDialog from './AutomaticRunDialog';
import {
  AutomaticRunHierarchyDifference,
  AutomaticRunLogItemCreate,
  AutomaticRunLogItemUpdate,
  AutomaticRunLogRemovedItem,
  AutomaticRunLogReturnedItem,
  AutomaticRunMissingHierarchy,
} from '../../../../shared/types/automaticRun';
import _ from 'lodash';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type AuditLogProps = {
  newState: {
    hierarchyDifference: AutomaticRunHierarchyDifference[];
    missingHierarchies: AutomaticRunMissingHierarchy[];
    removedItems: AutomaticRunLogRemovedItem[];
    returnedItems: AutomaticRunLogReturnedItem[];
    itemUpdates: AutomaticRunLogItemUpdate[];
    newItems: AutomaticRunLogItemCreate[];
  };
};

export type AuditLogEntry = {
  id: number;
  eventType: AuditEventType;
  eventDateTime: Date;
  userEmail: string;
  props?: AuditLogProps;
};

export enum DialogType {
  NEW,
  CHANGES,
  MISSING_HIERACHY,
  DIFFERENCES,
  REMOVAL,
  RETURNED,
}

const getEventTypeLabel = (eventType: AuditEventType) => {
  switch (eventType) {
    case 'automaticPimUpdater':
      return 'Tuotteiden päivitys';
    case 'automaticItemRemoval':
      return 'Tuotteiden poisto';
    default:
      return 'Automaattiajon tyyppi puuttuu!';
  }
};

const getIcon = (eventType: AuditEventType) => {
  switch (eventType) {
    case 'automaticPimUpdater':
      return <AddCircleIcon color="success" sx={{ fontSize: 16 }} />;
    case 'automaticItemRemoval':
      return <RemoveCircleIcon color="error" sx={{ fontSize: 16 }} />;
    default:
      return 'Automaattiajon tyyppi puuttuu!';
  }
};

const AutomaticRuns = () => {
  const [auditLogs, isLoading, error] = useApi<AuditLogEntry[]>(
    '/auditlog/logs?filterByType=automatic',
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [automaticRunId, setAutomaticRunId] = useState<number>();
  const [dialogType, setDialogType] = useState<DialogType>();

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenDialog = (id: number, dialogType: DialogType) => {
    setAutomaticRunId(id);
    setDialogOpen(true);
    setDialogType(dialogType);
  };

  const formatCellLabel = (label: string, data: any[]) =>
    `${label}: ${data.length}`;

  return (
    <>
      {error && (
        <Alert severity="error">Virhe ladattaessa muutoslokia</Alert>
      )}
      {isLoading === LoadingState.IsLoading && <CircularProgress />}
      {!error && (
        <>
          <Alert severity="info">
            Näytetään viimeiset 1000 tapahtumaa.
          </Alert>
          <TableContainer>
            <Table
              style={{ minWidth: 650 }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>Tyyppi</TableCell>
                  <TableCell>Aikaleima</TableCell>
                  <TableCell>Muutokset</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs?.map((row, i) => (
                  <TableRow key={row.eventType + i}>
                    <TableCell>
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        gap={1}
                      >
                        {getIcon(row.eventType)}
                        {getEventTypeLabel(row.eventType)}
                      </Box>
                    </TableCell>
                    <TableCell component="th">
                      {formatDateTime(row.eventDateTime)}
                    </TableCell>
                    <TableCell>
                      {[
                        {
                          dialogType: DialogType.NEW,
                          columnKey: 'newItems',
                          label: 'Uusia tuotteita',
                          type: 'update',
                        },
                        {
                          dialogType: DialogType.CHANGES,
                          columnKey: 'itemUpdates',
                          label: 'Muuttuneita tuotteita',
                          type: 'update',
                        },
                        {
                          dialogType: DialogType.MISSING_HIERACHY,
                          columnKey: 'missingHierarchies',
                          label: 'Puuttuva hierarkia',
                          type: 'update',
                        },
                        {
                          dialogType: DialogType.REMOVAL,
                          columnKey: 'removedItems',
                          label: 'Poistetut tuotteet',
                          type: 'removal',
                        },
                        {
                          dialogType: DialogType.RETURNED,
                          columnKey: 'returnedItems',
                          label: 'Palautetut tuotteet',
                          type: 'removal',
                        },
                      ].map((column) => {
                        if (
                          (column.type === 'removal' &&
                            row.eventType ===
                              'automaticPimUpdater') ||
                          (column.type === 'update' &&
                            row.eventType === 'automaticItemRemoval')
                        ) {
                          return null;
                        }
                        const values = _.get(
                          row,
                          'props.newState.' + column.columnKey,
                          [],
                        );
                        const isNotEmpty = values.length > 0;
                        return (
                          <Typography
                            key={column.columnKey}
                            sx={{
                              cursor: isNotEmpty ? 'pointer' : '',
                            }}
                            onClick={() => {
                              if (isNotEmpty) {
                                handleOpenDialog(
                                  row.id,
                                  column.dialogType,
                                );
                              }
                            }}
                          >
                            <Box
                              display={'flex'}
                              gap={1}
                              alignItems={'center'}
                            >
                              {formatCellLabel(column.label, values)}
                              {isNotEmpty && (
                                <OpenInNewIcon
                                  color={'primary'}
                                  sx={{ fontSize: 18 }}
                                />
                              )}
                            </Box>
                          </Typography>
                        );
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AutomaticRunDialog
            dialogType={dialogType}
            handleClose={handleCloseDialog}
            open={dialogOpen}
            auditLogs={auditLogs}
            automaticRunId={automaticRunId}
          />
        </>
      )}
    </>
  );
};

export default AutomaticRuns;
